import { calculatePAC } from "@/lib/calculatePAC";
import { calculatePOD } from "@/lib/calculatePOD";
import {
  ingredientDerivationSchema,
  ingredientWriteSchema,
  type IngredientDerivation,
  type ParsedIngredientWrite,
} from "@scoopulator/api-contracts";

export {
  ingredientDerivationSchema,
  type IngredientDerivation,
} from "@scoopulator/api-contracts";
export const ingredientDraftSchema = ingredientWriteSchema;
export type IngredientDraft = ParsedIngredientWrite;
export type IngredientValidationIssue = {
  message: string;
  path: PropertyKey[];
};
export type IngredientPreparationResult = {
  values: IngredientDraft;
  issues: IngredientValidationIssue[];
};
export function sodiumToSaltFraction(sodiumMgPer100g: number) {
  return (sodiumMgPer100g / 100_000) * 2.5;
}
export function prepareIngredient(
  input: IngredientDraft,
  inputs: IngredientDerivation = {},
): IngredientPreparationResult {
  const checkedInputs = ingredientDerivationSchema.safeParse(inputs);
  if (!checkedInputs.success)
    return {
      values: { ...input },
      issues: checkedInputs.error.issues.map((issue) => ({
        message: issue.message,
        path: issue.path,
      })),
    };
  inputs = checkedInputs.data;
  const derivation = {
    ...inputs,
    saltPct:
      inputs.sodiumMgPer100g === undefined
        ? inputs.saltPct
        : sodiumToSaltFraction(inputs.sodiumMgPer100g),
  };
  const values = { ...input };
  if (derivation.waterByDifference && derivation.ashPct !== undefined) {
    values.waterPct =
      1 -
      values.fatPct -
      values.proteinPct -
      values.carbPct -
      values.alcoholPct -
      derivation.ashPct;
  }
  if (derivation.sugarBreakdown) {
    values.pac = calculatePAC({
      ...derivation.sugarBreakdown,
      salt: derivation.saltPct ?? 0,
      alcohol: values.alcoholPct,
    });
    values.pod = calculatePOD(derivation.sugarBreakdown);
  }
  const parsed = ingredientDraftSchema.safeParse(values);
  const issues: IngredientValidationIssue[] = parsed.success
    ? []
    : parsed.error.issues.map((issue) => ({
        message: issue.message,
        path: issue.path,
      }));
  const fail = (message: string) => issues.push({ message, path: [] });
  // Detailed composition checks require supplied derivation information. Legacy and advanced entries retain explicit values.
  if (Object.keys(inputs).length) {
    const epsilon = 1e-6;
    if (
      values.waterPct < 0 ||
      values.waterPct > 1 ||
      (derivation.saltPct ?? 0) > 1
    )
      fail("Invalid water or salt fraction; correct the composition.");
    if (values.saturatedFatPct + values.transFatPct > values.fatPct + epsilon)
      fail("Saturated and trans fat exceed total fat.");
    if (values.sugarsPct + values.fiberPct > values.carbPct + epsilon)
      fail("Sugars and fiber exceed total carbohydrate.");
    if (values.lactosePct > values.sugarsPct + epsilon)
      fail("Lactose exceeds total sugars.");
    if (
      derivation.sugarBreakdown &&
      Math.abs(
        Object.values(derivation.sugarBreakdown).reduce(
          (a, b) => a + (b ?? 0),
          0,
        ) - values.sugarsPct,
      ) > epsilon
    )
      fail("Sugar breakdown must equal total sugars.");
    if (
      derivation.sugarBreakdown &&
      Math.abs((derivation.sugarBreakdown.lactose ?? 0) - values.lactosePct) >
        epsilon
    )
      fail("Lactose must agree with sugar breakdown.");
    if (
      derivation.saltPct !== undefined &&
      derivation.ashPct !== undefined &&
      derivation.saltPct > derivation.ashPct + epsilon
    )
      fail("Salt exceeds total mineral estimate.");
    const mass =
      values.waterPct +
      values.fatPct +
      values.proteinPct +
      values.carbPct +
      values.alcoholPct +
      (derivation.ashPct ?? 0);
    if (derivation.ashPct !== undefined && Math.abs(mass - 1) > 0.005)
      fail(
        "Water, fat, protein, carbohydrate, alcohol, and ash must total 100% (within 0.5% label rounding). Correct inconsistent source data rather than silently adjusting it.",
      );
    if (
      values.cocoaFatPct > values.fatPct + epsilon ||
      values.cocoaSolidsNonfatPct >
        1 - values.waterPct - values.fatPct + epsilon
    )
      fail("Cocoa components exceed available fat or nonfat solids.");
    if (
      values.emulsifierPct + values.stabilizerPct >
      1 - values.waterPct + epsilon
    )
      fail("Emulsifier and stabilizer exceed total solids.");
  }
  return { values, issues };
}
